import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { GlobalMessagingSeverityConstants } from 'app/shared/generated/generated'
import { ApiValidationErrors } from 'app/shared/models/handled-error'
import { AppConfigService } from 'app/shared/services/app-config.service'
import { MessageBusService } from 'app/shared/services/message-bus.service'
import { catchError, Observable, tap, throwError } from 'rxjs'
import { UIMesssageConstants } from './../../shared/generated/generated'
import { HandledError } from './../../shared/models/handled-error'

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  public constructor(
    private router: Router,
    private messageBusService: MessageBusService,
    private appConfigService: AppConfigService
  ) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event: HttpEvent<any>) => {
        this.notifyHttpRequestAnnoyer(event)
        this.handleModelValidationErrorErrors(event)
      }),

      catchError((err: HttpErrorResponse) => {
        if ((err as unknown as HandledError)?.hasAlreadyBeenHandled) {
          if (err as unknown as ApiValidationErrors) {
            return throwError(() => err as unknown as ApiValidationErrors)
          }
          return throwError(() => err as unknown as HandledError)
        }

        let errorMessage = ''
        let isServerError: Boolean = false

        if (err.error instanceof ErrorEvent) {
          //client-side
          errorMessage = err.message
        } else {
          //server-side
          isServerError = true
          errorMessage = this.getErrorMessageFromApiReturn(err)
        }

        this.notifyUserOfError(errorMessage)
        this.notifyApiErrorAnnoyer(errorMessage, isServerError)
        this.notifyAppOffline(err)

        let handledError = new HandledError(errorMessage)

        return throwError(() => handledError)
      })
    )
  }

  notifyUserOfError(errorMessage: string) {
    this.messageBusService.addToast(
      this.messageBusService.createMessage(GlobalMessagingSeverityConstants.error, 'Error', errorMessage)
    )
  }

  getErrorMessageFromApiReturn(err: any) {
    let errorMessage = ''
    let statusCode = err.status
    let errorMessageToDisplay = ''

    if (err.error == null) {
      errorMessageToDisplay = err.message
    }
    if (err.error?.Errors != null) {
      errorMessageToDisplay = err.error?.Errors.map((data) => data.Message).join(',')
    }

    if (this.appConfigService.areDetailedErrorsEnabled()) {
      if (statusCode > 0) {
        errorMessage = `${statusCode} - ${errorMessageToDisplay}`
      } else {
        errorMessage = `${errorMessageToDisplay}`
      }
    } else {
      errorMessage = `${errorMessageToDisplay}`
    }
    return errorMessage
  }

  handleModelValidationErrorErrors(event: HttpEvent<any>) {
    if (event instanceof HttpResponse) {
      if (event.ok && event?.body?.succeeded === false && event.body?.errors[0]?.type == 'ApiValidationErrorResponse') {
        let error = new ApiValidationErrors()
        error.hasAlreadyBeenHandled = true
        error.validationErrors = []
        error.message = UIMesssageConstants.validationErrorGeneric
        Object.keys(event.body.result).forEach((fieldName) => {
          let validationMessage = { fieldName: fieldName, validationMessage: event.body.result[fieldName] }
          error.validationErrors.push(validationMessage)
        })

        this.messageBusService.addToast(
          this.messageBusService.createMessage(GlobalMessagingSeverityConstants.error, error.message, '')
        )
        throw error
      }
    }
  }

  notifyHttpRequestAnnoyer(event: HttpEvent<any>) {
    if (this.appConfigService.areDetailedErrorsEnabled()) {
      if (event instanceof HttpResponse) {
        let debugMessage = this.messageBusService.createMessage(
          GlobalMessagingSeverityConstants.info,
          `${event.url.replace(this.appConfigService.getApiUrl(), '')}`,
          '',
          1000
        )
        debugMessage.key = 'debuggingToast'
        debugMessage.closable = false
        this.messageBusService.addToast(debugMessage)
      }
    }
  }

  notifyApiErrorAnnoyer(errorMessage, isServerError: Boolean) {
    if (this.appConfigService.areDetailedErrorsEnabled()) {
      let apiAnnoyer = `${errorMessage}, <ul class='mt-5 text-left'><li>At ${
        window.location
      }</li><li>On ${new Date().toLocaleTimeString()}</li><li>In HttpErrorInterceptor</li><li>Is Server Error: ${isServerError}</li></ul>`

      this.messageBusService.addNotification(
        this.messageBusService.createMessage(
          GlobalMessagingSeverityConstants.error,
          '😈 <b>API</b> Dev Annoyer 😈',
          apiAnnoyer
        )
      )
    }
  }

  notifyAppOffline(err: HttpErrorResponse) {
    if (err.status === 0) {
      this.messageBusService.addNotification(
        this.messageBusService.createMessage(
          GlobalMessagingSeverityConstants.error,
          `Server offline at ${new Date().toLocaleTimeString()}`,
          UIMesssageConstants.genericException
        ),
        false
      )
      this.router.navigate(['/auth/login'])
    }
  }
}
